import React from 'react';
import styled from 'styled-components';
import DatePicker from 'react-date-picker';
import { createTimeInstance } from '@utils/time';
import { OCCASION_WINDOW_PRESETS } from './windowPresets';
import { Body5, VisuallyHidden } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';

const FieldSet = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
`;

const PresetRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;

  /* Compact "small" chip treatment for the preset SkeletonButtons */
  button {
    padding: 4px 12px;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const DatePickerWrapper = styled.div`
  .react-date-picker__wrapper {
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    border-radius: 4px;
    padding: 8px;
    background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  }

  .react-date-picker__inputGroup__input {
    color: ${({ theme }) => theme.themeColors.text};
  }

  .react-calendar {
    background: ${({ theme }) => theme.themeColors.appBackground};
    color: ${({ theme }) => theme.themeColors.text};
  }

  .react-calendar button {
    background: ${({ theme }) => theme.themeColors.appBackground} !important;
    color: ${({ theme }) => theme.themeColors.text} !important;
  }

  .react-calendar button:enabled:hover {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton} !important;
    color: ${({ theme }) => theme.themeColors.buttonText} !important;
  }

  .react-calendar__tile--active {
    background: ${({ theme }) => theme.themeColors.primaryCtaButton} !important;
    color: ${({ theme }) => theme.themeColors.buttonText} !important;
  }
`;

// The preset arrays live in ./windowPresets — data, with no React or stylesheet in its import graph,
// so the feed viewer's pure modules can read them without pulling in this widget. Re-exported here
// so existing importers of DateWindow keep working; consumers move over in a follow-up.
//
// OCCASION_WINDOW_PRESETS is imported above as well as re-exported here: `export … from` creates no
// local binding, and this component uses it as its own default `presets` value.
export { KNOWABLE_WINDOW_PRESETS, LOADED_WINDOW_PRESETS } from './windowPresets';
export { OCCASION_WINDOW_PRESETS };

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return createTimeInstance(a).format('YYYY-MM-DD') === createTimeInstance(b).format('YYYY-MM-DD');
};

export const DateWindow = ({
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  maxDate,
  minDate,
  maxWindowYears = 15,
  presets = OCCASION_WINDOW_PRESETS,
  legend = 'Occasion window',
  // The window's end is the upper bound and not the analyst's to choose: a backfill stamped at
  // scoring time ends the moment it is requested. Only the start date is shown. Presets still
  // resolve from the upper bound and still report it through onToChange, so a caller that keeps a
  // toDate keeps a sane one; which preset reads as pressed follows the start date alone, since the
  // toDate the caller holds is off screen and may be stale or unset.
  toFixed = false,
}) => {
  // Resolve the upper bound: prefer the passed maxDate, fall back to "now".
  const to = maxDate || createTimeInstance().toDate();

  // Clamp a candidate "from" instance so it never predates minDate.
  const clampFrom = (instance) => {
    if (minDate && instance.isBefore(createTimeInstance(minDate))) {
      return minDate;
    }
    return instance.toDate();
  };

  // What a preset resolves its lower bound to. `null` means "no lower bound", which only a
  // `clears` Max produces.
  const resolveFrom = (preset) => {
    const upper = createTimeInstance(to);
    if (preset.amount) return clampFrom(upper.subtract(preset.amount, preset.unit));
    if (preset.key === 'ytd') return clampFrom(upper.startOf('year'));
    // A one-day window on the upper bound itself. No clamp: the upper bound cannot predate the
    // lower one, and clamping it would silently widen the narrowest preset on offer.
    if (preset.key === 'latest') return upper.startOf('day').toDate();
    if (preset.clears) return null;
    return minDate || upper.subtract(maxWindowYears, 'year').toDate();
  };

  const hasCustomPreset = presets.some((preset) => preset.key === 'custom');

  // The pickers are always visible unless the preset row offers Custom. When it does they start
  // hidden, except on a window that is already set — that window came from somewhere this
  // component cannot see (a restored URL, a previous session), so showing what it is beats
  // implying a preset produced it.
  const [showPickers, setShowPickers] = React.useState(() => !hasCustomPreset || !!(fromDate || toDate));

  const isActive = (preset) => {
    if (preset.key === 'custom') return showPickers;
    if (preset.clears) return !fromDate && !toDate;
    if (!fromDate) return false;
    if (toFixed) return isSameDay(fromDate, resolveFrom(preset));
    if (!toDate) return false;
    return isSameDay(toDate, to) && isSameDay(fromDate, resolveFrom(preset));
  };

  const handlePreset = (preset) => {
    if (preset.key === 'custom') {
      setShowPickers(true);
      return;
    }
    const from = resolveFrom(preset);
    onFromChange(from);
    onToChange(from === null ? null : to);
  };

  return (
    <FieldSet>
      {/* A <legend>, not a <span>: it names the <fieldset> it opens, which is how the grouping is
          announced at all. The shared component renders a span by default. */}
      <VisuallyHidden as="legend">{legend}</VisuallyHidden>
      <PresetRow>
        {presets.map((preset) => (
          <SkeletonButton
            key={preset.key}
            onClick={() => handlePreset(preset)}
            buttonProps={{ 'aria-pressed': isActive(preset) }}
          >
            {preset.label}
          </SkeletonButton>
        ))}
      </PresetRow>
      {showPickers && (
        <Row>
          <div role="group" aria-label="Start date">
            <DatePickerWrapper>
              <DatePicker
                name="from-date"
                onChange={onFromChange}
                value={fromDate}
                minDate={minDate}
                maxDate={maxDate}
              />
            </DatePickerWrapper>
          </div>
          {!toFixed && (
            <>
              <Body5 bold aria-hidden="true">
                –
              </Body5>
              <div role="group" aria-label="End date">
                <DatePickerWrapper>
                  <DatePicker name="to-date" onChange={onToChange} value={toDate} minDate={minDate} maxDate={maxDate} />
                </DatePickerWrapper>
              </div>
            </>
          )}
        </Row>
      )}
    </FieldSet>
  );
};
